/**
 * Supervisor stem-render API surface.
 *
 * Stems are per-character WAV deliverables, video-length, with reference
 * timecode preserved. Render is a long-running Modal job; this module gives
 * the SPA the four endpoints it needs:
 *
 *   1. List per-character render status + latest version.
 *   2. Trigger a (re)render for one character.
 *   3. Trigger a bulk render for a whole project.
 *   4. Bundle every approved stem into a downloadable .zip with a signed URL.
 *
 * The actual heavy work happens server-side; everything here is an HTTP shim
 * suitable for use with TanStack Query.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export type StemRenderStatus = 'idle' | 'queued' | 'rendering' | 'ready' | 'failed'

/**
 * One row per character on the stem download portal. `latest_version` is null
 * until the first successful render.
 */
export interface CharacterStem {
  character_id: string
  character_code: string
  character_display_name: string
  actor_display_name: string | null
  approved_takes: number
  total_cues: number
  pickup_pending: number
  latest_version: number | null
  status: StemRenderStatus
  /** Signed URL to the rendered WAV. Expires; refetch the list to refresh. */
  download_url: string | null
  /** Signed URL to the QC PDF. */
  qc_pdf_url: string | null
  rendered_at: string | null
  rendered_by_email: string | null
}

export interface ProjectStemsList {
  characters: readonly CharacterStem[]
}

export interface BundleDownload {
  /** Signed URL to a project-wide .zip of every ready stem. */
  url: string
  /** Number of stems in the bundle. */
  stem_count: number
  /** Total bytes (best estimate). */
  size_bytes: number
  /** ISO-8601 expiry. */
  expires_at: string
}

const stemsKey = (projectId: string) => ['supervisor', 'stems', projectId] as const

/** List per-character stem rows for a project. */
export function useProjectStems(projectId: string) {
  return useQuery({
    queryKey: stemsKey(projectId),
    queryFn: () => api<ProjectStemsList>(`/api/v1/projects/${projectId}/stems`),
    enabled: projectId.length > 0,
  })
}

/** Kick off (or re-kick) a render for one character. Idempotent server-side. */
export function useRenderCharacterStem(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (characterId: string) =>
      api<{ status: StemRenderStatus }>(
        `/api/v1/projects/${projectId}/characters/${characterId}/stem/render`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stemsKey(projectId) })
    },
  })
}

/** Render every character at once. */
export function useRenderAllStems(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api<{ queued: number }>(`/api/v1/projects/${projectId}/stems/render-all`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stemsKey(projectId) })
    },
  })
}

/** Bundle all ready stems into a .zip and return a signed download URL. */
export function useBundleDownload(projectId: string) {
  return useMutation({
    mutationFn: () =>
      api<BundleDownload>(`/api/v1/projects/${projectId}/stems/download`, {
        method: 'POST',
      }),
  })
}
